import { Period, StripePlanId } from "./types";

export type Plan = {
  /** Backend contract id sent to webOnboardingTrialStart (resolveStripePlanId). */
  planId: StripePlanId;
  /** Billing period — drives the Apple Pay recurring interval and period strings. */
  period: Period;
  /** Card heading, e.g. "12 months". */
  title: string;
  /** Sub-line under the title, e.g. "$159.99 billed annually". */
  billedLabel: string;
  /** Whole-dollar monthly-equivalent price, shown as "$13/mo". */
  monthlyPrice: number;
  /** Post-trial charge in dollars — used for the Apple Pay recurring amount. */
  amount: number;
  /** Highlighted "best value" plan, pre-selected by default. */
  bestValue?: boolean;
};

// The slice of a plan the Apple Pay recurring request needs (amount + period).
export type PlanBilling = Pick<Plan, "amount" | "period">;

// Hardcoded for the experiment phase. The post-trial charge is enforced
// server-side from the planId; these amounts and labels are display copy only.
export const PLANS: Plan[] = [
  {
    planId: "annual",
    period: "yearly",
    title: "12 months",
    billedLabel: "$159.99 billed annually",
    monthlyPrice: 13,
    amount: 159.99,
    bestValue: true,
  },
  {
    planId: "semestral",
    period: "semestral",
    title: "6 months",
    billedLabel: "$109.99 every 6 months",
    monthlyPrice: 18,
    amount: 109.99,
  },
  {
    planId: "quarterly",
    period: "quarterly",
    title: "3 months",
    billedLabel: "$69.99 every 3 months",
    monthlyPrice: 23,
    amount: 69.99,
  },
];

export const DEFAULT_PLAN: Plan = PLANS.find((plan) => plan.bestValue) ?? PLANS[0];

const PLAN_IDS = PLANS.map((plan) => plan.planId);

/** Coerce a URL param (or any value) to a known plan id, mirroring the backend. */
export const parseStripePlanId = (
  value: string | null | undefined
): StripePlanId | undefined =>
  PLAN_IDS.includes(value as StripePlanId) ? (value as StripePlanId) : undefined;
